import { useState, useEffect } from 'react'
import { useAuth } from '../../../hooks/useAuth'
import { identityService } from '../../../shared/api'
import { Button } from '../../../components'
import { useToast } from '../../../components/ToastProvider'
import { useTenantBranding } from '../../../contexts/TenantContext.jsx'
import { Reply, AlertTriangle, Info } from 'lucide-react'

/**
 * Where a customer's reply to a document email goes.
 *
 * WHAT THIS IS NOT: a mailbox we host. Tynvara sends mail and cannot receive it — there is no MX
 * record on any of our domains and the DNS automation deliberately refuses to create one. This
 * field stores an address the TENANT already owns and reads, and puts it in the Reply-To header.
 *
 * WHY IT MATTERS: with nothing set, a hand-sent document replies to whoever pressed the button —
 * exposing one person's address on every document that leaves — and an automatic one carries no
 * reply address at all, while several of our templates invite the reader to reply. The product was
 * asking for a reply it had nowhere to put.
 *
 * The notes under the field come from the SERVER rather than being written here, for the same
 * reason the field-ticket switches serve their labels: the screen cannot then drift from what the
 * backend actually does with an empty value.
 */
// The tenant this page is editing. NOT `user.tenant_id` — that field does not exist. The login
// response is `UserResponse`, which declares no tenant id at all (schemas/auth.py), so Pydantic
// drops it and the browser never sees one; the tenant id lives only inside the signed JWT. Reading
// it off `user` yielded `undefined` for EVERY role, which then got spelled into the request URL as
// the literal string "undefined" and came back 403.
//
// `'master'` is not a tenant either: on the platform-admin host TenantContext sets that literal
// (TenantContext.jsx:44), and sending it would 403 the same way. These cards edit ONE company's
// settings, so on a host that names no company there is nothing for them to edit.
function useEditableTenantId() {
  const branding = useTenantBranding()
  const id = branding?.tenantId
  return id && id !== 'master' ? id : null
}

export default function ReplyToSettings() {
  const { isAdmin } = useAuth()
  const toast = useToast()
  const tenantId = useEditableTenantId()

  const [value, setValue] = useState('')
  const [saved, setSaved] = useState('')
  const [fallbackNote, setFallbackNote] = useState('')
  const [effectNote, setEffectNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState(null)

  const canEdit = isAdmin && isAdmin()
  const dirty = value.trim() !== saved.trim()

  useEffect(() => {
    if (!tenantId) {
      // NOT a silent empty card. Rendering nothing reads as 'this is switched off',
      // which is a different and more misleading statement than 'we cannot tell'.
      setLoadError('These settings belong to one company. Open this page on that '
                   + "company's own site to change them.")
      setLoading(false)
      return
    }
    let cancelled = false
    identityService.getReplyTo(tenantId)
      .then(data => {
        if (cancelled) return
        const current = data.reply_to || ''
        setValue(current)
        setSaved(current)
        setFallbackNote(data.fallback_note || '')
        setEffectNote(data.takes_effect_note || '')
      })
      .catch(err => {
        if (cancelled) return
        // Named, not swallowed. An empty box on a failed load reads as "no reply address is set",
        // which is a different and more alarming statement than "we could not check".
        console.error('Failed to load the reply-to setting', err)
        setLoadError('Could not load this setting. Refresh to try again.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [tenantId])

  const save = async () => {
    if (!tenantId) {
      // Belt and braces: the load effect already refuses, but the two guards were
      // never the same guard, which is how a URL containing "undefined" reached
      // the server at all.
      toast.error('This page is not showing one company, so there is nothing to save to.')
      return
    }
    setSaving(true)
    try {
      const res = await identityService.updateReplyTo(tenantId, value.trim())
      const stored = res.reply_to || ''
      setValue(stored)
      setSaved(stored)
      toast.success(stored
        ? 'Replies will go to ' + stored + '.'
        : 'Reply address cleared.')
    } catch (err) {
      console.error('Failed to save the reply-to setting', err)
      // The server's message is written for a non-technical admin and says what to do about it —
      // preferring it to a generic string is the whole reason it is worded that way.
      toast.error(err?.response?.data?.detail || 'Could not save that address.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Reply size={18} className="text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">Where customer replies go</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        When you email a customer a field ticket, proposal or invoice and they press Reply, this is
        the address it reaches. Use a mailbox more than one of your people can open — a shared
        inbox like <span className="font-mono text-xs">office@yourcompany.com</span> — so an answer
        is never sitting in one person’s email where nobody else can find it.
      </p>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {loadError && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {!loading && !loadError && (
        <>
          <label className="block">
            <span className="block text-sm font-medium text-gray-900 mb-1">Reply-to address</span>
            <input
              type="email"
              value={value}
              disabled={!canEdit || saving}
              onChange={(e) => setValue(e.target.value)}
              placeholder="office@yourcompany.com"
              className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm
                         focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500
                         disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>

          {!saved && fallbackNote && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>{fallbackNote}</span>
            </div>
          )}

          <div className="mt-3 flex items-start gap-2 text-xs text-gray-600">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>
              This must be a mailbox somebody actually reads — a no-reply address will be refused,
              because pointing replies at one loses them. We do not host it: it stays in your own
              email system and you keep control of it.
              {effectNote ? ' ' + effectNote : ''}
            </span>
          </div>

          {canEdit && (
            <div className="mt-4 flex items-center gap-3">
              <Button variant="primary" onClick={save} disabled={saving || !dirty}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
              {!dirty && saved && (
                <span className="text-xs text-gray-500">Replies go to {saved}</span>
              )}
            </div>
          )}
          {!canEdit && (
            <p className="mt-4 text-xs text-gray-500">
              You need administrator access to change this.
            </p>
          )}
        </>
      )}
    </div>
  )
}
